import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';

import { useProfileMenuV2 } from '../providers/ProfileMenuV2Provider';
import { ProfilesType } from '../types';

import { useAnalytics } from './useAnalytics';
import { useRedirectToUL } from './useRedirectToUL';

export function compareProfiles(currentProfiles: ProfilesType, newProfiles: ProfilesType) {
  if (currentProfiles.length === 0) return true;

  if (currentProfiles.length !== newProfiles.length) return false;
  return currentProfiles.every((profile, i) => profile.email === newProfiles[i].email);
}

// This hook is to resolve the issue of the user switch account on one tab and the other tab not updating
export function useSyncBrowserTab() {
  const redirectToUL = useRedirectToUL();
  const { logEvent } = useAnalytics();

  const { selectedProfile, otherProfiles, userSwitcherEnabled, getSyncProfileCallback } =
    useProfileMenuV2();

  const currentProfiles = useMemo(
    // selectedProfile will be empty object, then we don't want to compare until it's assigned value.
    () => {
      if (!selectedProfile || !otherProfiles) return [];

      return Object.keys(selectedProfile ?? {}).length === 0
        ? []
        : [selectedProfile, ...otherProfiles];
    },
    [selectedProfile, otherProfiles],
  );

  const pollingStart = useRef<boolean>(false);
  const checkReloadTimeout = useRef<number>(-1);

  const polling = useCallback(
    async (counter = 0) => {
      pollingStart.current = true;

      const profiles = await getSyncProfileCallback();

      if (!compareProfiles(profiles ?? [], currentProfiles)) {
        logEvent(
          'user_switcher_redirect_to_ul',
          {
            action: ActionType.view,
            componentType: ComponentType.dropdown,
            currentProfilesCount: currentProfiles.length,
            newProfilesCount: profiles?.length ?? 0,
          },
          AnalyticsEventImportance.high,
        );
        redirectToUL();
        checkReloadTimeout.current = -1;
      } else if (counter < 2) {
        // polling for 2 times, 1500ms each, total 2 * (1500ms + api call 200ms) = 3.4s
        checkReloadTimeout.current = window.setTimeout(() => {
          void polling(counter + 1);
        }, 1500);
      } else {
        checkReloadTimeout.current = -1;
      }
      pollingStart.current = false;
    },
    [currentProfiles, getSyncProfileCallback, redirectToUL, logEvent],
  );

  const focusHandler = useCallback(() => {
    if (!pollingStart.current) {
      void polling();
    }
  }, [polling]);

  const blurHandler = useCallback(() => {
    if (checkReloadTimeout.current !== -1) {
      clearTimeout(checkReloadTimeout.current);
      checkReloadTimeout.current = -1;
      pollingStart.current = false;
    }
  }, []);

  useEffect(() => {
    if (userSwitcherEnabled) {
      window.addEventListener('focus', focusHandler);
      window.addEventListener('blur', blurHandler);
    } else {
      window.removeEventListener('focus', focusHandler);
      window.removeEventListener('blur', blurHandler);
    }

    return () => {
      window.removeEventListener('focus', focusHandler);
      window.removeEventListener('blur', blurHandler);
    };
  }, [blurHandler, focusHandler, userSwitcherEnabled]);
}
